import { BarChart3, CalendarRange, ChevronRight, CircleAlert, RefreshCcw, Sparkles, Target, TimerReset, Trophy } from "lucide-react";
import { Link, Navigate, Route, Routes } from "react-router";
import { useState } from "react";
import AdsIntelligenceDashboard from "./ads/index";
import AdAlertsPage from "./ads/alerts";
import PlatformConnectionPage from "./ads/connect";
import ReportBuilderPage from "./reports";
import ProofOfValuePage from "../reports/pov/index";
import ProductReportSourcePage from "./report-sources";
import McpAnalyticsPage from "./mcp-analytics";
import BenchmarkExplorerPage from "./benchmarks";
import AnalyticsDashboardPage from "./dashboard";
import CohortAnalyticsPage from "./cohorts";
import { useApi } from "@/hooks/use-api";
import { formatCurrency as formatMoney } from "@/lib/i18n";
import { SkeletonCard } from "@/components/skeleton";

type AttributionModel = "last_touch" | "first_touch" | "linear" | "time_decay" | "position_based";

interface AttributionDashboardData {
  totals: {
    spendCents: number;
    trueRevenueCents: number;
    platformRevenueCents: number;
    trueRoas: number;
    platformRoas: number;
    overcountFactor: number;
    cpl: number;
    leads: number;
  };
  byPlatform: Array<{
    platform: string;
    spendCents: number;
    trueRoas: number;
    platformRoas: number;
    cpl: number;
    leads: number;
  }>;
}

interface AttributionAccountData {
  id: string;
  platform: string;
  accountName: string;
  status: string;
}

interface RoiAnalyticsData {
  totalSpendCents: number;
  totalRevenueCents: number;
  overallRoas: number;
  overallCplCents: number;
  overallCpaCents: number;
  totalLeads: number;
  totalConversions: number;
  rows: Array<{
    campaignId: string | null;
    campaignName: string | null;
    channel: string;
    spendCents: number;
    revenueCents: number;
    trueRoas: number;
    cplCents: number;
    cpaCents: number;
    leads: number;
    conversions: number;
  }>;
}

interface LeadSourceData {
  total: number;
  sources: Array<{
    source: string;
    medium: string;
    leads: number;
    conversions: number;
    conversionRate: number;
    revenueCents: number;
  }>;
}

interface VelocityData {
  stages: Array<{
    stageId: string;
    stageName: string;
    avgDays: number;
    dealCount: number;
    isBottleneck: boolean;
    benchmarkAvgDays: number | null;
  }>;
  overallAvgDays: number;
  bottleneckStage: string | null;
}

interface BenchmarkData {
  comparisons: Array<{
    metricName: string;
    yourValue: number;
    industryAvg: number | null;
    top10Percent: number | null;
    isAboveAvg: boolean;
    percentile: number | null;
  }>;
}

interface AttributionEvidenceData {
  model: string;
  coverage: {
    touchpoints: number;
    identifiedTouchpoints: number;
    campaignTouchpoints: number;
    conversions: number;
  };
  touchpoints: Array<{
    id: string;
    contactId: string | null;
    anonymousId: string | null;
    eventType: string;
    channel: string | null;
    campaignName: string | null;
    campaignPlatform: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    landingUrl: string | null;
    touchedAt: string;
  }>;
  conversions: Array<{
    id: string;
    contactId: string;
    dealId: string | null;
    conversionEventType: string;
    valueCents: number;
    currency: string;
    touchpointCount: number;
    creditedTouchpoints: number;
    campaignTags: string[];
    convertedAt: string;
  }>;
}

const PLATFORM_LABELS: Record<string, string> = {
  meta: "Meta",
  google: "Google",
  tiktok: "TikTok",
};

const ATTRIBUTION_MODE_OPTIONS: Array<{ key: AttributionModel; label: string }> = [
  { key: "last_touch", label: "Last touch" },
  { key: "first_touch", label: "First touch" },
  { key: "linear", label: "Linear" },
  { key: "time_decay", label: "Time decay" },
  { key: "position_based", label: "Position based" },
];

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatDelta(trueRoas: number, platformRoas: number): string {
  const delta = platformRoas - trueRoas;
  if (delta <= 0.05) {
    return "Aligned to CRM";
  }
  return `+${delta.toFixed(2)}x vs platform`;
}

function formatMediumLabel(medium: string): string {
  return medium
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatPercentile(percentile: number | null): string {
  if (percentile === null) {
    return "Industry percentile unavailable";
  }

  const mod10 = percentile % 10;
  const mod100 = percentile % 100;
  const suffix = mod10 === 1 && mod100 !== 11 ? "st" : mod10 === 2 && mod100 !== 12 ? "nd" : mod10 === 3 && mod100 !== 13 ? "rd" : "th";
  return `${percentile}${suffix} percentile`;
}

function formatBenchmarkLabel(metricName: string): string {
  switch (metricName) {
    case "roas":
      return "TRUE ROAS";
    case "cplCents":
      return "Cost per lead";
    case "cpaCents":
      return "Cost per acquisition";
    case "conversionRate":
      return "Conversion rate";
    default:
      return metricName;
  }
}

function buildAttributionQuery(dateRange: { from: string; to: string }, model: AttributionModel): string {
  return new URLSearchParams({
    from: dateRange.from,
    to: dateRange.to,
    model,
  }).toString();
}

export function AttributionAnalyticsPage() {
  const [model, setModel] = useState<AttributionModel>("last_touch");
  const [dateRange, setDateRange] = useState(() => ({
    from: getDefaultFrom(),
    to: getDefaultTo(),
  }));

  const attributionQuery = buildAttributionQuery(dateRange, model);
  const dashboardQuery = useApi<AttributionDashboardData>("/v1/ads/dashboard");
  const accountsQuery = useApi<AttributionAccountData[]>("/v1/ads/accounts");
  const roiQuery = useApi<RoiAnalyticsData>(`/v1/analytics/attribution/roi?${attributionQuery}&group_by=campaign`);
  const sourcesQuery = useApi<LeadSourceData>(`/v1/analytics/attribution/sources?${attributionQuery}`);
  const evidenceQuery = useApi<AttributionEvidenceData>(`/v1/analytics/attribution/evidence?${attributionQuery}&limit=8`);
  const velocityQuery = useApi<VelocityData>(`/v1/analytics/attribution/velocity?${attributionQuery}`);
  const benchmarkQuery = useApi<BenchmarkData>(`/v1/analytics/attribution/benchmarks?${attributionQuery}`);

  const isLoading = [
    dashboardQuery,
    accountsQuery,
    roiQuery,
    sourcesQuery,
    evidenceQuery,
    velocityQuery,
    benchmarkQuery,
  ].some((query) => query.isLoading);

  const dashboard = dashboardQuery.data;
  const roi = roiQuery.data;
  const sources = sourcesQuery.data?.sources ?? [];
  const sourceTotal = sourcesQuery.data?.total ?? 0;
  const evidence = evidenceQuery.data;
  const hasEvidence = Boolean(evidence && (evidence.touchpoints.length > 0 || evidence.conversions.length > 0));
  const velocity = velocityQuery.data;
  const benchmarks = benchmarkQuery.data?.comparisons ?? [];
  const accounts = accountsQuery.data ?? [];
  const connectedAccounts = accounts.filter((account) => account.status === "connected");
  const hasConnectedAccounts = connectedAccounts.length > 0;
  const campaignRows = roi?.rows ?? [];

  const accountsError = accountsQuery.error;
  const roiError = roiQuery.error;
  const sourceError = sourcesQuery.error;
  const evidenceError = evidenceQuery.error;
  const velocityError = velocityQuery.error;
  const benchmarkError = benchmarkQuery.error;
  const dashboardError = dashboardQuery.error;
  const errorMessages = [dashboardError, roiError, sourceError, evidenceError, velocityError, benchmarkError].filter(Boolean);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
            <RefreshCcw className="h-4 w-4 animate-spin" />
            Loading attribution evidence
          </div>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Checking mounted attribution summaries, source mix, and evidence coverage.
          </p>
        </section>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <SkeletonCard
              key={index}
              className="h-28 rounded-2xl border-[var(--color-border)] bg-[var(--color-card)]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (accountsError && accounts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-6">
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 h-5 w-5 text-[var(--color-destructive)]" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Attribution data is unavailable</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              The mounted ads account contract failed before attribution truth could be assembled.
            </p>
            <p className="text-sm text-[var(--color-muted-foreground)]">Error: {accountsError}</p>
            <button
              type="button"
              onClick={() => {
                accountsQuery.refetch();
                dashboardQuery.refetch();
                roiQuery.refetch();
                sourcesQuery.refetch();
                evidenceQuery.refetch();
                velocityQuery.refetch();
                benchmarkQuery.refetch();
              }}
              className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-foreground)] px-4 py-2 text-sm font-medium text-[var(--color-background)] transition hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 active:scale-[0.99]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasConnectedAccounts) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-card)] p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-3">
              <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/8 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                <Sparkles className="h-3.5 w-3.5" />
                Revenue Attribution
              </span>
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold tracking-[-0.02em] text-[var(--color-foreground)]">
                  Connect ad platforms before attribution can tell the truth
                </h1>
                <p className="max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
                  This mounted surface only reports CRM-backed revenue after a real ad account is connected. No platform is connected for this tenant yet, so the dashboard stops at setup instead of faking numbers.
                </p>
              </div>
            </div>
            <Link
              to="/analytics/ads/connect"
              className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-background)] transition hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 active:scale-[0.99]"
            >
              Connect ad platforms
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="overflow-hidden rounded-[28px] border border-[var(--color-border)] bg-[linear-gradient(135deg,var(--color-card),var(--color-surface))] p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--color-primary)]/15 bg-[var(--color-card)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
              <BarChart3 className="h-3.5 w-3.5" />
              Revenue Attribution
            </span>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-[-0.025em] text-[var(--color-foreground)] sm:text-4xl">
                CRM-backed revenue from live ad spend, without attribution theater
              </h1>
              <p className="max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)] sm:text-base">
                The immediate job is clear: which channels bring trustworthy revenue, how the selected attribution model shifts credit, and where velocity bottlenecks still slow down real revenue.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 sm:min-w-[18rem] sm:grid-cols-2">
            <AttributionHeroStat label="Connected channels" value={String(connectedAccounts.length)} />
            <AttributionHeroStat label="Attributed campaigns" value={String(campaignRows.length)} />
          </div>
        </div>

        <div className="mt-5 grid gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
              <CalendarRange className="h-3.5 w-3.5" />
              Date window
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                aria-label="Attribution start date"
                type="date"
                value={dateRange.from}
                onChange={(event) => setDateRange((current) => ({ ...current, from: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2"
              />
              <input
                aria-label="Attribution end date"
                type="date"
                value={dateRange.to}
                onChange={(event) => setDateRange((current) => ({ ...current, to: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
              <Target className="h-3.5 w-3.5" />
              Attribution model
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ATTRIBUTION_MODE_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={option.key === model}
                  onClick={() => setModel(option.key)}
                  className={`rounded-xl border px-3 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 active:scale-[0.99] ${
                    option.key === model
                      ? "border-[var(--color-primary)]/35 bg-[var(--color-primary)]/10 text-[var(--color-foreground)] shadow-sm"
                      : "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/20 hover:text-[var(--color-foreground)]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[var(--color-warning)]/25 bg-[var(--color-warning)]/8 p-4">
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 h-5 w-5 text-[var(--color-warning)]" />
          <div className="space-y-1">
            <p className="font-medium text-[var(--color-foreground)]">
              {hasEvidence ? "Contact-level attribution evidence mounted" : "Partial attribution evidence"}
            </p>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {hasEvidence
                ? "Recent touchpoints and conversions now expose contact IDs, campaign tags, and UTM provenance for attribution drill-down."
                : "Contact-level UTM provenance is not mounted yet, so this surface stays explicit about campaign credit and source mix without implying per-contact traceability."}
            </p>
          </div>
        </div>
      </section>

      {errorMessages.length > 0 && (
        <section className="rounded-2xl border border-[var(--color-warning)]/25 bg-[var(--color-warning)]/8 p-4">
          <div className="flex items-start gap-3">
            <CircleAlert className="mt-0.5 h-5 w-5 text-[var(--color-warning)]" />
            <div className="space-y-1">
              <p className="font-medium text-[var(--color-foreground)]">Partial attribution data</p>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Some mounted revenue data failed to load. The page is showing only the sections backed by successful live responses.
              </p>
              <p className="text-sm text-[var(--color-muted-foreground)]">{errorMessages.join(" | ")}</p>
            </div>
          </div>
        </section>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <AttributionMetricCard label="Ad Spend" value={dashboard ? formatMoney(dashboard.totals.spendCents, "EUR", { maximumFractionDigits: dashboard.totals.spendCents % 100 === 0 ? 0 : 2 }) : "Unavailable"} tone="default" />
        <AttributionMetricCard label="CRM Revenue" value={dashboard ? formatMoney(dashboard.totals.trueRevenueCents, "EUR", { maximumFractionDigits: dashboard.totals.trueRevenueCents % 100 === 0 ? 0 : 2 }) : roi ? formatMoney(roi.totalRevenueCents, "EUR", { maximumFractionDigits: roi.totalRevenueCents % 100 === 0 ? 0 : 2 }) : "Unavailable"} tone="primary" />
        <AttributionMetricCard label="TRUE ROAS" value={dashboard ? `${dashboard.totals.trueRoas.toFixed(2)}x` : roi ? `${roi.overallRoas.toFixed(2)}x` : "Unavailable"} tone="primary" />
        <AttributionMetricCard label="Cost Per Lead" value={dashboard ? formatMoney(dashboard.totals.cpl, "EUR", { maximumFractionDigits: dashboard.totals.cpl % 100 === 0 ? 0 : 2 }) : roi ? formatMoney(roi.overallCplCents, "EUR", { maximumFractionDigits: roi.overallCplCents % 100 === 0 ? 0 : 2 }) : "Unavailable"} tone="default" />
      </section>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr] xl:grid-cols-[1.35fr_0.95fr]">
        <div className="space-y-4">
          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
            <div className="flex flex-col gap-2 border-b border-[var(--color-border)] pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Revenue by source</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Mounted attribution sources grouped by channel and medium, with no fake contact-level provenance.
                </p>
              </div>
            </div>

            {sourceError ? (
              <UnavailablePanel
                title="Source breakdown unavailable"
                description="Mounted source evidence failed to load for this tenant and date range."
              />
            ) : sources.length === 0 ? (
              <UnavailablePanel
                title="Waiting for source-level revenue"
                description="The mounted source contract is live, but no tenant-scoped source rows are available yet."
              />
            ) : (
              <div className="mt-4 space-y-3">
                {sources.map((source) => (
                  <article
                    key={`${source.source}-${source.medium}`}
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-base font-semibold text-[var(--color-foreground)]">{source.source}</p>
                        <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{formatMediumLabel(source.medium)}</p>
                      </div>
                      <div className="rounded-full bg-[var(--color-primary)]/8 px-3 py-1 text-sm font-semibold text-[var(--color-primary)]">
                        {formatPercent(source.conversionRate)} conversion rate
                      </div>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <CompactMetric label="Revenue" value={formatMoney(source.revenueCents, "EUR", { maximumFractionDigits: source.revenueCents % 100 === 0 ? 0 : 2 })} emphasize />
                      <CompactMetric label="Conversions" value={String(source.conversions)} />
                      <CompactMetric label="Leads" value={String(source.leads)} />
                      <CompactMetric
                        label="Source share"
                        value={sourceTotal > 0 ? formatPercent(source.leads / sourceTotal) : "Unavailable"}
                      />
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
            <div className="flex flex-col gap-2 border-b border-[var(--color-border)] pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Top attributed campaigns</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Campaign credit shifts with the selected attribution model, but stays grounded in mounted CRM revenue.
                </p>
              </div>
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                {ATTRIBUTION_MODE_OPTIONS.find((option) => option.key === model)?.label}
              </p>
            </div>

            {roiError ? (
              <UnavailablePanel
                title="Campaign attribution unavailable"
                description="The mounted ROI contract did not return campaign rows for this model and date range."
              />
            ) : campaignRows.length === 0 ? (
              <UnavailablePanel
                title="Waiting for campaign attribution"
                description="The mounted ROI contract is live, but no attributed campaign rows are available yet."
              />
            ) : (
              <div className="mt-4 space-y-3">
                {campaignRows.map((campaign) => (
                  <article
                    key={campaign.campaignId ?? `${campaign.channel}-${campaign.campaignName ?? "unknown"}`}
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 transition hover:border-[var(--color-primary)]/35 hover:shadow-md"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-base font-semibold text-[var(--color-foreground)]">{campaign.campaignName ?? "Unnamed campaign"}</p>
                        <p className="text-xs font-medium uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                          {PLATFORM_LABELS[campaign.channel] ?? campaign.channel}
                        </p>
                      </div>
                      <div className="rounded-full bg-[var(--color-primary)]/8 px-3 py-1 text-sm font-semibold text-[var(--color-primary)]">
                        {campaign.trueRoas.toFixed(2)}x TRUE ROAS
                      </div>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <CompactMetric label="Revenue" value={formatMoney(campaign.revenueCents, "EUR", { maximumFractionDigits: campaign.revenueCents % 100 === 0 ? 0 : 2 })} emphasize />
                      <CompactMetric label="Spend" value={formatMoney(campaign.spendCents, "EUR", { maximumFractionDigits: campaign.spendCents % 100 === 0 ? 0 : 2 })} />
                      <CompactMetric label="Leads" value={String(campaign.leads)} />
                      <CompactMetric label="Conversions" value={String(campaign.conversions)} />
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>

        <section className="space-y-4">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Connected channels</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Live ad account truth used by this attribution surface.
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {connectedAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-[var(--color-foreground)]">{account.accountName}</p>
                    <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                      {PLATFORM_LABELS[account.platform] ?? account.platform}
                    </p>
                  </div>
                  <span className="rounded-full bg-[var(--color-success)]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-success)]">
                    Live
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <TimerReset className="h-4 w-4 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Pipeline velocity</h2>
            </div>
            <div className="mt-4 space-y-3">
              {velocityError ? (
                <UnavailablePanel
                  title="Pipeline velocity unavailable"
                  description="The mounted velocity contract failed to load for this tenant and date range."
                />
              ) : velocity && velocity.stages.length > 0 ? (
                <>
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Overall average</p>
                    <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{velocity.overallAvgDays.toFixed(0)} days</p>
                  </div>
                  {velocity.stages.slice(0, 3).map((stage) => (
                    <div
                      key={stage.stageId}
                      className={`rounded-2xl border px-4 py-3 ${stage.isBottleneck ? "border-[var(--color-warning)]/25 bg-[var(--color-warning)]/8" : "border-[var(--color-border)] bg-[var(--color-background)]"}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-[var(--color-foreground)]">{stage.stageName}</p>
                          <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{stage.dealCount} deals sampled</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[var(--color-foreground)]">{stage.avgDays.toFixed(0)} days</p>
                          {stage.isBottleneck && <p className="text-xs font-medium text-[var(--color-warning)]">Current bottleneck</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <UnavailablePanel
                  title="Pipeline velocity attribution is not mounted yet"
                  description="Velocity joins between campaign influence and deal-stage timing are still absent from the live analytics runtime."
                />
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Benchmark rail</h2>
            </div>
            <div className="mt-4 space-y-3">
              {benchmarkError ? (
                <UnavailablePanel
                  title="Benchmark evidence unavailable"
                  description="Industry comparison failed to load for this tenant and date range."
                />
              ) : benchmarks.length === 0 ? (
                <UnavailablePanel
                  title="Benchmark evidence not mounted yet"
                  description="Industry comparisons remain unavailable until tenant industry and benchmark data are both live."
                />
              ) : (
                benchmarks.slice(0, 3).map((comparison) => (
                  <div key={comparison.metricName} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-[var(--color-foreground)]">{formatBenchmarkLabel(comparison.metricName)}</p>
                        <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{formatPercentile(comparison.percentile)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[var(--color-foreground)]">{comparison.yourValue}</p>
                        <p className={`text-xs font-medium ${comparison.isAboveAvg ? "text-[var(--color-success)]" : "text-[var(--color-warning)]"}`}>
                          {comparison.isAboveAvg ? "Above industry avg" : "Below industry avg"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Evidence rail</h2>
            <div className="mt-4 space-y-3">
              {evidenceError ? (
                <UnavailablePanel
                  title="Attribution evidence unavailable"
                  description="Touchpoint provenance failed to load, so this rail is withholding drill-down claims."
                />
              ) : hasEvidence && evidence ? (
                <AttributionEvidenceRail evidence={evidence} />
              ) : (
                <UnavailablePanel
                  title="Contact-level UTM provenance is not mounted yet"
                  description="The mounted attribution contracts do not expose lead/contact evidence, campaign tags, or touchpoint histories yet."
                />
              )}
              <Link
                to="/analytics/ads"
                className="inline-flex min-h-11 w-full items-center justify-between rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm font-medium text-[var(--color-foreground)] transition hover:border-[var(--color-primary)]/35 hover:text-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 active:scale-[0.99]"
              >
                Open Ads Intelligence
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function AttributionEvidenceRail({ evidence }: { evidence: AttributionEvidenceData }) {
  const touchpoints = evidence.touchpoints.slice(0, 3);
  const conversions = evidence.conversions.slice(0, 2);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <CompactMetric label="Touchpoints" value={String(evidence.coverage.touchpoints)} />
        <CompactMetric label="Conversions" value={String(evidence.coverage.conversions)} />
        <CompactMetric label="Identified" value={String(evidence.coverage.identifiedTouchpoints)} />
        <CompactMetric label="Campaign-tagged" value={String(evidence.coverage.campaignTouchpoints)} />
      </div>

      {touchpoints.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Recent touchpoints
          </p>
          {touchpoints.map((touchpoint) => (
            <article key={touchpoint.id} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[var(--color-foreground)]">
                    {touchpoint.campaignName ?? touchpoint.utmCampaign ?? touchpoint.eventType}
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {touchpoint.eventType} · {touchpoint.channel ?? touchpoint.campaignPlatform ?? "unknown"} · {formatEvidenceDate(touchpoint.touchedAt)}
                  </p>
                  <p className="mt-1 truncate text-xs text-[var(--color-muted-foreground)]">
                    UTM: {touchpoint.utmCampaign ?? "none"} · Contact: {formatEvidenceEntity(touchpoint.contactId, touchpoint.anonymousId)}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {conversions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Recent conversions
          </p>
          {conversions.map((conversion) => (
            <article key={conversion.id} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--color-foreground)]">
                    {conversion.conversionEventType} · {formatMoney(conversion.valueCents, conversion.currency, { maximumFractionDigits: conversion.valueCents % 100 === 0 ? 0 : 2 })}
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {conversion.creditedTouchpoints}/{conversion.touchpointCount} credited touchpoints · {formatEvidenceDate(conversion.convertedAt)}
                  </p>
                  <p className="mt-1 truncate text-xs text-[var(--color-muted-foreground)]">
                    Campaign tags: {conversion.campaignTags.length > 0 ? conversion.campaignTags.join(", ") : "none"}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function formatEvidenceEntity(contactId: string | null, anonymousId: string | null): string {
  if (contactId) {
    return `contact ${contactId.slice(0, 8)}`;
  }
  return anonymousId ? `anon ${anonymousId.slice(0, 8)}` : "unknown";
}

function formatEvidenceDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().slice(0, 10);
}

function AttributionHeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{label}</p>
      <p className="text-2xl font-semibold tracking-[-0.02em] text-[var(--color-foreground)]">{value}</p>
    </div>
  );
}

function AttributionMetricCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "default" | "primary";
}) {
  return (
    <div className={`rounded-2xl border border-[var(--color-border)] p-5 ${tone === "primary" ? "bg-[var(--color-primary)]/6" : "bg-[var(--color-card)]"}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{label}</p>
      <p className={`mt-3 text-3xl font-semibold tracking-[-0.025em] ${tone === "primary" ? "text-[var(--color-primary)]" : "text-[var(--color-foreground)]"}`}>
        {value}
      </p>
    </div>
  );
}

function CompactMetric({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="rounded-xl bg-[var(--color-surface)] px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{label}</p>
      <p className={`mt-1 text-sm font-semibold ${emphasize ? "text-[var(--color-success)]" : "text-[var(--color-foreground)]"}`}>{value}</p>
    </div>
  );
}

function UnavailablePanel({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)]/60 p-4">
      <p className="font-medium text-[var(--color-foreground)]">{title}</p>
      <p className="mt-1 text-sm leading-6 text-[var(--color-muted-foreground)]">{description}</p>
    </div>
  );
}

function getDefaultFrom(): string {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
}

function getDefaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function AnalyticsPage() {
  return (
    <Routes>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<AnalyticsDashboardPage />} />
      <Route path="ads" element={<AdsIntelligenceDashboard />} />
      <Route path="ads/alerts" element={<AdAlertsPage />} />
      <Route path="ads/connect" element={<PlatformConnectionPage />} />
      <Route path="reports" element={<ReportBuilderPage />} />
      <Route path="reports/sources" element={<ProductReportSourcePage />} />
      <Route path="reports/pov" element={<ProofOfValuePage />} />
      <Route path="mcp" element={<McpAnalyticsPage />} />
      <Route path="attribution" element={<AttributionAnalyticsPage />} />
      <Route path="benchmarks" element={<BenchmarkExplorerPage />} />
      <Route path="cohorts" element={<CohortAnalyticsPage />} />
    </Routes>
  );
}
